import { mountVeivoWalkaround } from "@veivo/web-sdk";
import { useRef, useState } from "react";
import { Button, Modal, Spinner } from "@/components/atoms";
import { VeivoHelpModal, VeivoHelpTopic } from "../../VeivoHelp/VeivoHelpModal";
import { ContactSupport } from "../ContactSupport";
import { useVeivoPanel, VeivoSession } from "../useVeivoPanel";

/**
 * Step 2 (DEV-1902): the walk-around and VIN photo (Gate 2). On a computer,
 * Veivo's panel shows a code for the phone and follows the walk-around live.
 *
 * "On a phone or tablet already? Use this device's camera" opens the camera in
 * a full-screen modal on this page — no navigation, so an operator on a phone
 * or tablet never goes back and forth between a computer and a QR code. Its ✕
 * closes the modal and comes back here.
 */
export const WalkaroundStep = ({
  session,
  complete,
  rejected = false,
  onComplete,
  onBack,
  onNext,
  onFinishLater,
  saving,
}: {
  session: VeivoSession | null;
  /** The walk-around passed, as the server says. */
  complete: boolean;
  /** Refused after the walk-around: support can check it and let the
   *  operator continue (DEV-2002). */
  rejected?: boolean;
  onComplete: () => void;
  onBack: () => void;
  onNext: () => void;
  onFinishLater: () => void;
  saving: boolean;
}) => {
  const host = useRef<HTMLDivElement>(null);
  const [cameraOpen, setCameraOpen] = useState(false);
  const [helpTopic, setHelpTopic] = useState<VeivoHelpTopic | null>(null);

  useVeivoPanel(
    host,
    mountVeivoWalkaround,
    session,
    {
      onUseThisDevice: () => setCameraOpen(true),
      onComplete,
      onHelp: setHelpTopic,
    },
    // Rejected → anything else (staff let it continue): start the panel over.
    { remountKey: rejected ? "rejected" : "open" }
  );

  return (
    <div className="flex flex-col gap-6">
      <div ref={host} data-testid="veivo-walkaround" />
      {rejected ? (
        <ContactSupport emphasis lead="This walk-around couldn't be verified." tail="they can check it and let you continue." />
      ) : (
        <ContactSupport lead="Stuck on the walk-around?" />
      )}

      <div className="flex flex-col-reverse gap-3 xl:flex-row xl:justify-between">
        <Button variant="custom" size="xs" className="text-neutral-silver-gray underline xl:w-auto" disabled={saving} onClick={onFinishLater}>
          {saving ? <Spinner /> : "Save and finish later"}
        </Button>
        <div className="flex flex-col-reverse gap-3 xl:flex-row">
          <Button variant="secondary" size="lg" className="w-full xl:w-auto" onClick={onBack}>
            Back to documents
          </Button>
          <Button variant="primary" size="lg" className="w-full xl:w-auto" endIcon="ArrowRight" disabled={!complete} onClick={onNext}>
            Next
          </Button>
        </div>
      </div>

      <Modal
        open={cameraOpen}
        onClose={() => setCameraOpen(false)}
        title="Walk-around"
        variant="web-large"
        // The camera covers the whole screen; a transformed dialog would shrink
        // it to the dialog's box.
        allowFullScreenContent
      >
        {cameraOpen && (
          <CameraPanel
            session={session}
            onClose={() => setCameraOpen(false)}
            onComplete={() => {
              setCameraOpen(false);
              onComplete();
            }}
            onHelp={setHelpTopic}
          />
        )}
      </Modal>
      <VeivoHelpModal topic={helpTopic} onClose={() => setHelpTopic(null)} />
    </div>
  );
};

/** This device's camera, full-screen whatever the screen size. */
const CameraPanel = ({
  session,
  onClose,
  onComplete,
  onHelp,
}: {
  session: VeivoSession | null;
  onClose: () => void;
  onComplete: () => void;
  onHelp: (topic: VeivoHelpTopic) => void;
}) => {
  const host = useRef<HTMLDivElement>(null);
  useVeivoPanel(host, mountVeivoWalkaround, session, { onClose, onComplete, onHelp }, { fullscreen: true });
  return <div ref={host} data-testid="veivo-camera" />;
};
